<script setup>
import { nextTick } from 'vue'
import { useToast } from 'vue-toastification'
import { localeFlag, localeTitle, supportedLanguages } from '@/plugins/i18n'
import { useLanguageService } from '@/composables/language'
import { isUserLoggedIn, logoutHelper } from '@/auth/utils'
import { useAuthStore } from '@/stores/auth'
import vanIcon from '@/assets/images/van.svg'
import lorryIcon from '@/assets/images/lorry.svg'
import bigLorryIcon from '@/assets/images/big_lorry.svg'
import lkwIcon from '@/assets/images/lkw.svg'
import googleReviews from '@/assets/images/google_reviews.png'
import forbes from '@/assets/images/forbes.png'
import transportImage from '@/assets/images/transport.jpg'
import aboutUsImage from '@/assets/images/about_us.png'
import gallery1 from '@/assets/images/gallery_1.jpg'
import gallery2 from '@/assets/images/gallery_2.jpg'
import gallery3 from '@/assets/images/gallery_3.jpg'
import gallery4 from '@/assets/images/gallery_4.jpg'
import euroIcon from '@/assets/images/euro_icon.svg'
import mailIcon from '@/assets/images/mail_icon.svg'
import phoneIcon from '@/assets/images/phone_icon.svg'

const navItems = [
  'Home', 'About us', 'Clients', 'Carriers', 'Blog', 'Contact',
]

const topMenuItems = [
  'SwidAI',
  'transport@swida.sk',
  'InstaCarrier API',
  'InstaCarrier',
]

const vehicleTypes = [
  {
    icon: vanIcon,
    title: 'VAN',
    desc: 'Up to 1,2 tons',
  },
  {
    icon: lorryIcon,
    title: 'LORRY',
    desc: 'Up to 1,2 tons',
  },
  {
    icon: bigLorryIcon,
    title: 'BIG LORRY',
    desc: 'Up to 1,2 tons',
  },
  {
    icon: lkwIcon,
    title: 'LKW',
    desc: 'Up to 1,2 tons',
  },
]

const secondaryMenuItems = [
  {
    label: 'Contracted Express Transport',
    title: 'Contracted Express Transport',
    desc: 'Tento typ prepráv si vyžaduje rýchlu a precíznu prácu špeditérov. Sme si vedomí toho, že čas sú peniaze a v oblasti prepravy tovaru platí toto heslo dvojnásobne. Práve preto je našou prioritou, aby bol váš tovar na tom správnom mieste v čo najkratšom čase.',
    img: transportImage,
    ctaPrimary: { title: 'Send Us Your Request', url: '#' },
    ctaSecondary: { title: 'Contact Key Account', url: '#' },
  },
  {
    label: 'Regular Transport',
    title: 'Regular Transport',
    desc: 'Pravidelná preprava pre vaše stabilné trasy so spoľahlivými časmi doručenia a predvídateľnými nákladmi.',
    img: transportImage,
    ctaPrimary: { title: 'Send Us Your Request', url: '#' },
    ctaSecondary: { title: 'Contact Key Account', url: '#' },
  },
  {
    label: 'API/EDI Integrations',
    title: 'API/EDI Integrations',
    desc: 'Napojenie cien a objednávok cez API alebo EDI pre plne automatizovaný tok vašich dát.',
    img: transportImage,
    ctaPrimary: { title: 'Send Us Your Request', url: '#' },
    ctaSecondary: { title: 'Contact Key Account', url: '#' },
  },
  {
    label: 'Customs',
    title: 'Customs',
    desc: 'Colné služby a podpora pri odbavovaní tak, aby vaše zásielky plynulo prešli hranicami.',
    img: transportImage,
    ctaPrimary: { title: 'Send Us Your Request', url: '#' },
    ctaSecondary: { title: 'Contact Key Account', url: '#' },
  },
  {
    label: 'Shipment Tracking',
    title: 'Shipment Tracking',
    desc: 'End-to-end prehľad o zásielkach s notifikáciami a prístupom k aktuálnemu stavu.',
    img: transportImage,
    ctaPrimary: { title: 'Send Us Your Request', url: '#' },
    ctaSecondary: { title: 'Contact Key Account', url: '#' },
  },
]

const activeNav = ref(navItems[0])
const activeTopMenu = ref('InstaCarrier')

const botReplies = [
  'Lorem ipsum dolor',
  'Dolor sit amet',
  'Consectetur adipiscing elit',
]

const languageService = useLanguageService()
const toast = useToast()
const langMenuOpen = ref(false)
const currentLanguage = computed(() => languageService.currentLanguage.value)
const languages = supportedLanguages
const isMenuOpen = ref(false)
const userMenuOpen = ref(false)
const authStore = useAuthStore()
const isLoggedIn = computed(() => isUserLoggedIn())
const userEmail = computed(() => authStore.currentUser?.email || '')
const isInstaCarrier = computed(() => activeTopMenu.value === 'InstaCarrier')
const desktopTopMenuVariant = computed(() => (isInstaCarrier.value ? 'hero-top-menu--standard' : 'hero-top-menu--minimal'))
const desktopHeroPillVariant = computed(() => (isInstaCarrier.value ? 'hero-pill--standard' : 'hero-pill--minimal'))
const messageInput = ref('')
const messages = ref([])
const chatThreadRef = ref(null)
const chatThreadMobileRef = ref(null)
const isSpecialLayout = computed(() => ['transport@swida.sk', 'InstaCarrier API', 'SwidAI'].includes(activeTopMenu.value))
const activeSecondaryIndex = ref(0)
const activeSecondary = computed(() => secondaryMenuItems[activeSecondaryIndex.value]?.label || '')
const activeSecondaryData = computed(() => secondaryMenuItems[activeSecondaryIndex.value] || secondaryMenuItems[0])
const galleryImages = [gallery2, gallery3, gallery1, gallery4]
const activeGalleryIndex = ref(0)
const galleryTouchStartX = ref(null)

const contactMethods = [
  { icon: euroIcon, cta: 'GET YOUR PRICE' },
  { icon: phoneIcon, primary: 'info@swida.sk', secondary: '055/228 91 23' },
  { icon: mailIcon, cta: 'CONTACT US' },
]

function setActive(item) {
  activeNav.value = item
}

function toggleLangMenu() {
  langMenuOpen.value = !langMenuOpen.value
  userMenuOpen.value = false
}

async function setLanguage(lang) {
  langMenuOpen.value = false
  try {
    await languageService.setLanguage(lang)
  }
  catch (err) {
    console.error(err)
    toast.error('Could not set language. Try again later.')
  }
}

onMounted(() => {
  languageService.initializeLanguage()
})

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
  userMenuOpen.value = false
  langMenuOpen.value = false
}

function closeMenu() {
  isMenuOpen.value = false
  langMenuOpen.value = false
  userMenuOpen.value = false
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value
  langMenuOpen.value = false
}

function setActiveTopMenu(item) {
  activeTopMenu.value = item
}

function cycleTopMenu(direction) {
  const idx = topMenuItems.indexOf(activeTopMenu.value)
  const nextIdx = (idx + direction + topMenuItems.length) % topMenuItems.length

  activeTopMenu.value = topMenuItems[nextIdx]
}

function cycleSecondary(direction) {
  const nextIdx = (activeSecondaryIndex.value + direction + secondaryMenuItems.length) % secondaryMenuItems.length

  activeSecondaryIndex.value = nextIdx
}

function setActiveSecondary(item) {
  const idx = secondaryMenuItems.findIndex(entry => entry.label === item.label)

  activeSecondaryIndex.value = idx === -1 ? 0 : idx
}

function appendMessage(text, sender = 'bot') {
  messages.value.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text,
    sender,
  })
}

function scrollThreadToBottom() {
  nextTick(() => {
    [chatThreadRef.value, chatThreadMobileRef.value].forEach(el => {
      if (el) {
        const opts = { top: el.scrollHeight, behavior: 'smooth' }
        if (typeof el.scrollTo === 'function')
          el.scrollTo(opts)

        else
          el.scrollTop = el.scrollHeight
      }
    })
  })
}

function sendMessage() {
  const text = messageInput.value.trim()
  if (!text)
    return

  appendMessage(text, 'user')
  scrollThreadToBottom()
  messageInput.value = ''

  setTimeout(() => {
    const reply = botReplies[Math.floor(Math.random() * botReplies.length)]

    appendMessage(reply, 'bot')
    scrollThreadToBottom()
  }, 1000)
}

function setGalleryIndex(idx) {
  activeGalleryIndex.value = (idx + galleryImages.length) % galleryImages.length
}

function stepGallery(delta) {
  setGalleryIndex(activeGalleryIndex.value + delta)
}

function onGalleryTouchStart(event) {
  galleryTouchStartX.value = event.touches?.[0]?.clientX ?? null
}

function onGalleryTouchEnd(event) {
  if (galleryTouchStartX.value === null)
    return

  const endX = event.changedTouches?.[0]?.clientX ?? galleryTouchStartX.value
  const deltaX = endX - galleryTouchStartX.value
  const threshold = 30

  if (deltaX > threshold)
    stepGallery(-1)
  else if (deltaX < -threshold)
    stepGallery(1)

  galleryTouchStartX.value = null
}

watch(isLoggedIn, async loggedIn => {
  if (!loggedIn) {
    userMenuOpen.value = false

    return
  }

  if (!authStore.currentUser) {
    try {
      await authStore.fetchCurrentUser()
    }
    catch (err) {
      console.error(err)
    }
  }
}, { immediate: true })
</script>

<template>
  <!--  nav -->
  <nav class="w-full" :class="[(isMenuOpen || langMenuOpen || userMenuOpen) ? 'relative z-50' : 'relative']">
    <div class="w-[90%] lg:max-w-[69rem] mx-auto flex items-center justify-between">
      <!--      logo -->
      <div>
        <img src="@images/swida_portal_logo.svg" alt="Swida portal" class="w-[5.9375rem] h-auto">
      </div>

      <!--      items -->
      <div class="py-[0.75rem] lg:py-[0.85rem]">
        <!--        desktop -->
        <div class="hidden lg:flex items-center space-x-6">
          <div class="flex items-center space-x-8">
            <a
              v-for="(item, index) in navItems"
              :key="index"
              class="nav-link"
              :class="{ 'nav-link--active': activeNav === item }"
              @click="setActive(item)"
            >
              {{ item }}
            </a>
          </div>

          <RouterLink
            v-if="!isLoggedIn"
            :to="{ name: 'login' }"
            class="nav-link sign-in-btn"
          >
            {{ $t('Sign in') }}
          </RouterLink>
          <div v-else class="relative flex">
            <button
              type="button"
              class="nav-link sign-in-btn sign-in-btn--user"
              :aria-label="$t('User Menu')"
              :aria-expanded="userMenuOpen ? 'true' : 'false'"
              @click="toggleUserMenu"
            >
              <span class="sign-in-btn__text">{{ userEmail || $t('User Menu') }}</span>
              <svg
                class="sign-in-btn__chevron"
                :class="{ 'sign-in-btn__chevron--open': userMenuOpen }"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M5 8L10 13L15 8"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <div v-if="userMenuOpen" class="user-menu">
              <RouterLink
                class="user-menu__item"
                :to="{ path: '/account', query: { form: 'general' } }"
                @click="closeMenu"
              >
                {{ $t('Account settings') }}
              </RouterLink>
              <RouterLink
                class="user-menu__item"
                :to="{ path: '/account', query: { form: 'password' } }"
                @click="closeMenu"
              >
                {{ $t('Change password') }}
              </RouterLink>
              <button
                type="button"
                class="user-menu__item"
                @click="() => { closeMenu(); logoutHelper() }"
              >
                {{ $t('Sign Out') }}
              </button>
            </div>
          </div>

          <div class="relative flex">
            <button
              type="button"
              class="lang-btn"
              :aria-label="`Change language (current: ${localeTitle(currentLanguage)})`"
              @click="toggleLangMenu"
            >
              <span class="w-[1.8125rem] h-[1.8125rem] rounded-full" :class="`fi-${localeFlag(currentLanguage)}`" />
            </button>
            <div v-if="langMenuOpen" class="lang-menu">
              <button
                v-for="lang in languages"
                :key="lang"
                type="button"
                class="lang-menu__item"
                :class="{ 'lang-menu__item--active': lang === currentLanguage }"
                @click="setLanguage(lang)"
              >
                <span class="w-[1.8125rem] h-[1.8125rem] rounded-full" :class="`fi-${localeFlag(lang)}`" />
                <span>{{ localeTitle(lang) }}</span>
              </button>
            </div>
          </div>
        </div>

        <!--        mobile -->
        <div class="flex lg:hidden">
          <button
            type="button"
            class="lang-btn"
            aria-label="Toggle menu"
            @click="toggleMenu"
          >
            <img src="@images/hamburger.svg" alt="Toggle menu" class="w-[2rem] h-[2rem]">
          </button>
        </div>
      </div>
    </div>

    <!-- mobile menu -->
    <div
      class="lg:hidden w-[90%] mx-auto"
      :class="isMenuOpen ? 'mobile-menu mobile-menu--open' : 'mobile-menu'"
    >
      <div class="flex flex-col py-1 px-2 space-y-4 bg-white rounded">
        <a
          v-for="(item, index) in navItems"
          :key="`mobile-${index}`"
          class="nav-link nav-link--mobile"
          :class="{ 'nav-link--active': activeNav === item }"
          @click="() => { setActive(item); closeMenu() }"
        >
          {{ item }}
        </a>

        <RouterLink
          v-if="!isLoggedIn"
          :to="{ name: 'login' }"
          class="nav-link sign-in-btn mobile-sign-in"
          @click="closeMenu"
        >
          {{ $t('Sign in') }}
        </RouterLink>
        <div v-else>
          <button
            type="button"
            class="nav-link sign-in-btn mobile-sign-in sign-in-btn--user"
            :aria-label="$t('User Menu')"
            :aria-expanded="userMenuOpen ? 'true' : 'false'"
            @click="toggleUserMenu"
          >
            <span class="sign-in-btn__text">{{ userEmail || $t('User Menu') }}</span>
            <svg
              class="sign-in-btn__chevron"
              :class="{ 'sign-in-btn__chevron--open': userMenuOpen }"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 8L10 13L15 8"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div v-if="userMenuOpen" class="user-menu user-menu--mobile">
            <RouterLink
              class="user-menu__item"
              :to="{ path: '/account', query: { form: 'general' } }"
              @click="closeMenu"
            >
              {{ $t('Account settings') }}
            </RouterLink>
            <RouterLink
              class="user-menu__item"
              :to="{ path: '/account', query: { form: 'password' } }"
              @click="closeMenu"
            >
              {{ $t('Change password') }}
            </RouterLink>
            <button
              type="button"
              class="user-menu__item"
              @click="() => { closeMenu(); logoutHelper() }"
            >
              {{ $t('Sign Out') }}
            </button>
          </div>
        </div>

        <div class="relative">
          <button
            type="button"
            class="lang-btn"
            :aria-label="`Change language (current: ${localeTitle(currentLanguage)})`"
            @click="toggleLangMenu"
          >
            <span class="w-[1.8125rem] h-[1.8125rem] rounded-full" :class="`fi-${localeFlag(currentLanguage)}`" />
          </button>
          <div v-if="langMenuOpen" class="lang-menu lang-menu--mobile">
            <button
              v-for="lang in languages"
              :key="`mobile-lang-${lang}`"
              type="button"
              class="lang-menu__item"
              :class="{ 'lang-menu__item--active': lang === currentLanguage }"
              @click="setLanguage(lang)"
            >
              <span class="w-[1.8125rem] h-[1.8125rem] rounded-full" :class="`fi-${localeFlag(lang)}`" />
              <span>{{ localeTitle(lang) }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!--  hero -->
  <div class="flex flex-col-reverse lg:flex-row overflow-hidden lg:min-h-[43.375rem]">
    <div class="hidden lg:flex relative w-full lg:w-[45%] hero-left">
      <img
        src="@images/robot_human.png"
        alt="Swida portal"
        class="lg:absolute lg:-bottom-[40rem] lg:-right-[13.5rem] lg:w-[65rem] lg:max-w-none lg:h-auto pointer-events-none select-none z-50"
      >
    </div>

    <div class="relative z-10 w-full lg:w-[55%] hero-right">
      <div
        class="flex flex-col h-full p-[1rem] lg:p-[3rem] lg:min-h-[45rem] lg:max-w-[51rem] space-y-[2.5rem]"
        :class="[
          { 'lg:space-y-0 lg:justify-between': isSpecialLayout, 'lg:space-y-[2.5rem]': !isSpecialLayout },
        ]"
      >
        <!--          title -->
        <div class="text-center lg:text-left text-[3rem] leading-[1] uppercase font-['Inter'] text-[#363636] font-normal">
          On <span class="text-[#d91a5d] font-black">Demand </span> Express Transport
        </div>

        <!--          top menu mobile -->
        <div class="relative z-20 lg:w-full flex lg:hidden flex-col gap-2 text-xs md:text-sm lg:text-base leading-[1] font-['Inter'] font-medium text-[#363636] py-[0.5rem] px-[0.75rem] rounded-[36px]">
          <div class="flex w-full items-center justify-between gap-2">
            <button
              type="button"
              class="top-menu-arrow top-menu-arrow--mobile"
              aria-label="Previous"
              @click="cycleTopMenu(-1)"
            >
              <img src="@images/arrow_left.svg" alt="Previous" class="arrow-icon">
            </button>
            <button
              type="button"
              class="hero-pill hero-pill--standard hero-pill--active w-full justify-center"
              aria-pressed="true"
            >
              <span class="hero-pill__icon" aria-hidden="true" />
              <span>{{ activeTopMenu }}</span>
            </button>
            <button
              type="button"
              class="top-menu-arrow top-menu-arrow--mobile"
              aria-label="Next"
              @click="cycleTopMenu(1)"
            >
              <img src="@images/arrow_left.svg" alt="Next" class="arrow-icon rotate-180">
            </button>
          </div>
        </div>

        <!--        content above menu -->
        <div v-if="activeTopMenu !== 'InstaCarrier'">
          <div class="lg:max-w-[40rem] mx-auto">
            <div v-if="activeTopMenu === 'SwidAI'">
              <form action="" class="flex flex-col space-y-[1.4rem]">
                <div class="hidden lg:block">
                  <div
                    ref="chatThreadRef"
                    class="h-[12rem] w-full overflow-y-scroll chat-thread"
                  >
                    <div
                      v-for="message in messages"
                      :key="message.id"
                      class="chat-bubble"
                      :class="message.sender === 'user' ? 'chat-bubble--user' : 'chat-bubble--bot'"
                    >
                      {{ message.text }}
                    </div>
                  </div>
                </div>

                <div v-if="messages.length" class="lg:hidden">
                  <div
                    ref="chatThreadMobileRef"
                    class="h-[12rem] w-full overflow-y-scroll chat-thread"
                  >
                    <div
                      v-for="message in messages"
                      :key="message.id"
                      class="chat-bubble"
                      :class="message.sender === 'user' ? 'chat-bubble--user' : 'chat-bubble--bot'"
                    >
                      {{ message.text }}
                    </div>
                  </div>
                </div>

                <div class="message-card">
                  <textarea
                    v-model="messageInput"
                    class="message-card__textarea"
                    placeholder="Give me price..."
                    @keyup.enter.exact.prevent="sendMessage"
                  />
                  <button type="button" class="message-card__send-btn" @click="sendMessage">
                    Send
                  </button>
                </div>
              </form>
            </div>
            <div v-if="activeTopMenu === 'transport@swida.sk'">
              <div class="api-bubble">
                <p class="api-bubble__text">
                  Send your transportation request to
                </p>
                <p class="api-bubble__email">
                  quote@swida.sk
                </p>
              </div>
            </div>
            <div v-if="activeTopMenu === 'InstaCarrier API'">
              <div class="api-bubble">
                <p class="api-bubble__text">
                  Should you be interested in an <span class="api-bubble__highlight">API integration for instant pricing</span>,
                  please reach out to us at
                </p>
                <p class="api-bubble__email">
                  api@swida.sk
                </p>
              </div>
            </div>
          </div>
        </div>

        <!--          top menu desktop -->
        <div
          class="relative z-20 lg:w-full hidden lg:flex flex-col gap-2 text-xs md:text-sm lg:text-base leading-[1] font-['Inter'] font-medium text-[#363636] py-[0.5rem] px-[0.75rem] rounded-[36px] "
          :class="desktopTopMenuVariant"
        >
          <div class="w-full justify-center xl:justify-between items-center flex-wrap hidden lg:flex">
            <button
              v-for="item in topMenuItems"
              :key="item"
              type="button"
              class="hero-pill"
              :class="[
                desktopHeroPillVariant,
                {
                  'hero-pill--active': isInstaCarrier && activeTopMenu === item,
                  'hero-pill--minimal-active': !isInstaCarrier && activeTopMenu === item,
                },
              ]"
              @click="setActiveTopMenu(item)"
            >
              <span class="hero-pill__icon" aria-hidden="true" />
              <span>{{ item }}</span>
            </button>
          </div>
        </div>

        <!--        content under menu -->
        <div v-if="activeTopMenu === 'InstaCarrier'">
          <div class="lg:max-w-[40rem] mx-auto">
            <!--            available cars -->
            <div class="flex flex-col lg:flex-row space-y-[0.5rem] lg:space-y-0 lg:space-x-[0.5rem]">
              <div
                v-for="(vehicle, index) in vehicleTypes"
                :key="`vehicle-${index}`"
                class="w-full rounded-[16px] flex flex-col items-center justify-center space-y-[1rem] py-6 lg:p-2 text-center bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] lg:h-[133px]"
              >
                <img :src="vehicle.icon" :alt="vehicle.title" class="h-[1.25rem] w-auto">
                <div class="flex flex-col gap-1">
                  <span class="text-[14px] font-bold text-[#363636] leading-[1.5] text-center">{{ vehicle.title }}</span>
                  <span class="text-[11px] font-medium text-[#363636] leading-[1.5] text-center">{{ vehicle.desc }}</span>
                </div>
              </div>
            </div>
            <!--            contact form -->
            <div class="mt-[2rem]">
              <form action="" class="flex flex-col space-y-[1rem] lg:space-y-[2.5rem]">
                <div class="flex flex-col lg:flex-row gap-3">
                  <div class="contact-input flex-1 flex items-center gap-3">
                    <img src="@images/location.svg" alt="" class="w-[14.9px] h-[18.2px]">
                    <input type="text" placeholder="From" class="contact-input__inner">
                  </div>
                  <div class="contact-input flex-1 flex items-center gap-3">
                    <img src="@images/location.svg" alt="" class="w-[14.9px] h-[18.2px]">
                    <input type="text" placeholder="To" class="contact-input__inner">
                  </div>
                </div>

                <div>
                  <input type="email" placeholder="E-mail" class="contact-input">
                </div>

                <button type="button" class="cta-btn mx-auto">
                  GET YOUR PRICE
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--  what we do -->
  <div class="pt-[5rem] lg:pb-[13rem] flex flex-col justify-center items-center lg:max-w-[80rem] mx-auto">
    <h2 class="font-['Inter'] font-bold text-[48px] leading-[1] text-black text-center uppercase">
      what we do
    </h2>

    <div class="w-full mt-6">
      <!--      desktop navigation -->
      <div class="secondary-menu hidden lg:flex w-full">
        <button
          v-for="item in secondaryMenuItems"
          :key="item.label"
          type="button"
          class="secondary-menu__item"
          :class="{ 'secondary-menu__item--active': activeSecondary === item.label }"
          @click="setActiveSecondary(item)"
        >
          {{ item.label }}
        </button>
      </div>

      <!--      mobile navigation -->
      <div class="px-3 flex lg:hidden items-center gap-2">
        <button
          type="button"
          class="top-menu-arrow top-menu-arrow--mobile"
          aria-label="Previous"
          @click="cycleSecondary(-1)"
        >
          <img src="@images/arrow_left.svg" alt="Previous" class="arrow-icon">
        </button>
        <button
          type="button"
          class="secondary-menu__mobile-btn"
          aria-pressed="true"
        >
          {{ activeSecondary }}
        </button>
        <button
          type="button"
          class="top-menu-arrow top-menu-arrow--mobile"
          aria-label="Next"
          @click="cycleSecondary(1)"
        >
          <img src="@images/arrow_left.svg" alt="Next" class="arrow-icon rotate-180">
        </button>
      </div>

      <!--      content -->
      <div class="bg-[#f1f2f6] flex flex-col lg:flex-row items-center mt-[2rem] lg:rounded-[32px] overflow-hidden">
        <div class="w-full p-10 lg:p-0">
          <div class="lg:px-10 lg:w-[36rem] mx-auto">
            <div class="flex flex-col gap-4">
              <h3 class="font-['Inter'] font-bold text-[40px] leading-[1] uppercase text-[#363636]">
                {{ activeSecondaryData.title }}
              </h3>
              <p class="font-['Inter'] font-normal text-[16px] leading-[32px] text-[#363636]">
                {{ activeSecondaryData.desc }}
              </p>
              <div class="flex flex-col lg:flex-row flex-wrap gap-3">
                <a
                  :href="activeSecondaryData.ctaPrimary.url"
                  class="inline-flex items-center justify-center w-[236px] h-[57px] rounded-[32px] bg-[#d81b5d] text-white font-['Inter'] font-semibold text-[16px] leading-[1] whitespace-nowrap"
                  style="text-decoration: none; color: #ffffff;"
                >
                  {{ activeSecondaryData.ctaPrimary.title }}
                </a>
                <a
                  :href="activeSecondaryData.ctaSecondary.url"
                  class="inline-flex items-center justify-center w-[228px] h-[57px] rounded-[32px] border border-[#363636] bg-transparent text-[#363636] font-['Inter'] font-semibold text-[16px] leading-[1] whitespace-nowrap"
                  style="text-decoration: none; color: #363636;"
                >
                  {{ activeSecondaryData.ctaSecondary.title }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <img
          :src="activeSecondaryData.img"
          alt="Swida"
          class="block flex-shrink-0 w-full h-auto max-w-full lg:max-w-none lg:w-[539px] lg:min-h-[465px] lg:object-cover object-contain"
        >
      </div>
    </div>
  </div>

  <!--  awards -->
  <div class="relative z-10 -mt-12 lg:-mt-[133px] lg:-mb-[100px] lg:w-auto mx-auto bg-white shadow-[0px_-4px_76px_0px_#00000014] lg:rounded-[64px] py-6 lg:px-8">
    <div class="flex items-center justify-center gap-6 lg:gap-[83px] py-6 lg:py-[4.8rem]">
      <img :src="googleReviews" alt="Google Reviews" class="h-auto w-[86.5px] lg:w-[173px] max-w-full">
      <img :src="forbes" alt="Forbes" class="h-auto w-[101.5px] lg:w-[203px] max-w-full">
    </div>
  </div>

  <!--  about us -->
  <div
    class="bg-cover bg-center bg-no-repeat pt-[8rem] lg:pt-[12rem] pb-[5rem]"
    :style="{ backgroundImage: `url(${aboutUsImage})` }"
  >
    <div class="flex flex-col space-y-[3rem] lg:space-y-[4.3rem] w-full max-w-[68rem] px-4 sm:px-6 mx-auto text-center">
      <div>
        <h3 class="font-['Inter'] font-bold text-[32px] lg:text-[48px] leading-[1] text-white text-center uppercase">
          About us
        </h3>
      </div>

      <div class="flex flex-col lg:flex-row items-center justify-center gap-4">
        <div class="about-us__card">
          <div class="about-us__card-header">
            <span class="about-us__card-title">
              Transport solution
            </span>
            <img src="@images/right_arrow_white.svg" alt="" class="about-us__card-arrow">
          </div>
          <div class="about-us__card-value">
            1s
          </div>
        </div>

        <div class="about-us__card">
          <div class="about-us__card-header">
            <span class="about-us__card-title">Availability</span>
            <img src="@images/right_arrow_white.svg" alt="" class="about-us__card-arrow">
          </div>
          <div class="about-us__card-value">
            24/7
          </div>
        </div>
        <div class="about-us__card">
          <div class="about-us__card-header">
            <span class="about-us__card-title">Transport per year</span>
            <img src="@images/right_arrow_white.svg" alt="" class="about-us__card-arrow">
          </div>
          <div class="about-us__card-value">
            40K+
          </div>
        </div>
      </div>

      <div>
        <p class="font-['Inter'] font-normal text-[16px] leading-[1] text-center text-white lg:px-[8rem]">
          If you are interested in establishing cooperation or answering additional questions, do not hesitate to contact us via the contact form.
        </p>
      </div>

      <div>
        <button class="inline-flex items-center justify-center w-full sm:w-[236px] h-[57px] rounded-[32px] bg-[#d81b5d] text-white font-['Inter'] font-semibold text-[16px] leading-[1] mx-auto border-0 about-us__cta">
          Send Us Your Request
        </button>
      </div>
    </div>
  </div>

  <!--  gallery -->
  <div class="gallery">
    <div class="gallery__grid">
      <img :src="galleryImages[0]" alt="Team collaboration" class="gallery__img">
      <img :src="galleryImages[1]" alt="Office discussion" class="gallery__img">
      <img :src="galleryImages[2]" alt="Team portrait" class="gallery__img">
      <img :src="galleryImages[3]" alt="Project review" class="gallery__img">
    </div>

    <div
      class="gallery__mobile"
      @touchstart.passive="onGalleryTouchStart"
      @touchend.passive="onGalleryTouchEnd"
    >
      <img :src="galleryImages[activeGalleryIndex]" alt="" class="gallery__img gallery__img--mobile">
      <div class="gallery__dots">
        <button
          v-for="(img, idx) in galleryImages"
          :key="idx"
          type="button"
          class="gallery__dot"
          :class="{ 'gallery__dot--active': idx === activeGalleryIndex }"
          :aria-label="`Show gallery image ${idx + 1}`"
          @click="setGalleryIndex(idx)"
        />
      </div>
    </div>
  </div>

  <!--  contact -->
  <div class="contact flex flex-col space-y-[2rem]">
    <div>
      <h4 class="contact__title">
        Stay connected with SWIDA
      </h4>
    </div>

    <div>
      <p class="contact__desc">
        Choose the way that suits you best
      </p>
    </div>

    <div class="contact__methods">
      <div
        v-for="method in contactMethods"
        :key="method.icon"
        class="contact__card"
      >
        <img :src="method.icon" :alt="method.label" class="contact__icon">
        <template v-if="method.cta">
          <button
            type="button"
            class="contact__card-btn"
          >
            {{ method.cta }}
          </button>
        </template>
        <template v-else-if="method.primary">
          <div class="contact__card-text">
            <div class="contact__card-text-primary">
              {{ method.primary }}
            </div>
            <div class="contact__card-text-secondary">
              {{ method.secondary }}
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.nav-link {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 16px;
  line-height: 1;
  letter-spacing: 0;
  color: #101010;
}

.nav-link--active {
  font-weight: 700;
}

.nav-link--mobile {
  display: inline-block;
  width: 100%;
  padding: 0.25rem 0;
}

.sign-in-btn {
  width: 85px;
  height: 29px;
  border-radius: 23px;
  background: #d81b5d;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.sign-in-btn--user {
  width: 160px;
  padding: 0 12px;
  gap: 8px;
  justify-content: space-between;
  border: none;
  cursor: pointer;
}

.sign-in-btn__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sign-in-btn__chevron {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  transition: transform 150ms ease;
}

.sign-in-btn__chevron--open {
  transform: rotate(180deg);
}

.mobile-sign-in {
  width: 100%;
  height: auto;
  padding: 0.65rem 0.9rem;
}

.user-menu {
  position: absolute;
  top: 130%;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  z-index: 50;
  min-width: 12rem;
}

.user-menu--mobile {
  position: static;
  top: auto;
  right: auto;
  box-shadow: none;
}

.user-menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.25rem;
  width: 100%;
  text-align: left;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #111827;
  border: none;
  background: transparent;
  text-decoration: none;
  cursor: pointer;
}

.user-menu__item:hover {
  text-decoration: none;
}

.lang-btn {
  width: 29px;
  height: 29px;
  padding: 0;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.lang-menu {
  position: absolute;
  top: 130%;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  z-index: 50;
  min-width: 10rem;
}

.lang-menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.35rem 0.25rem;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #111827;
}

.lang-menu__item--active {
  font-weight: 700;
}

.mobile-menu {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
  transition: max-height 200ms ease, opacity 200ms ease, transform 200ms ease;
  margin-top: 0.25rem;
}

.mobile-menu--open {
  max-height: 320px;
  opacity: 1;
  transform: translateY(0);
  overflow: visible;
}

.lang-menu--mobile {
  right: auto;
  left: 0;
}

.hero-left {
  background: #d61b5c;
  background-image: url('@/assets/images/smart_background.jpg');
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: cover;
  background-blend-mode: multiply;
}

.hero-right {
  background: #f1f2f6;
}

.hero-top-menu--standard {
  background: #ffffff;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.04);
}

.hero-top-menu--minimal {
  background: transparent;
  box-shadow: none;
}

.hero-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0.75rem;
  border-radius: 66px;
  color: #363636;
  transition: background-color 150ms ease, color 150ms ease;
  cursor: pointer;
  border: none;
  background: transparent;
  appearance: none;
}

.hero-pill__icon {
  width: 1.25rem;
  height: 1.25rem;
  display: inline-block;
  background-color: currentColor;
  mask: url('@/assets/images/gear.svg') center/contain no-repeat;
  -webkit-mask: url('@/assets/images/gear.svg') center/contain no-repeat;
}

.hero-pill--standard.hero-pill--active,
.hero-pill--standard:hover {
  background: #d81b5d;
  color: #ffffff;
}

.hero-pill--minimal {
  background: transparent;
}

.hero-pill--minimal-active {
  color: #d81b5d;
  background: transparent;
}

.hero-pill--minimal:hover {
  color: #d81b5d;
  background: transparent;
}

.top-menu-arrow {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.top-menu-arrow--mobile {
  width: 2rem;
  height: 2rem;
}

.top-menu-arrow--mobile .arrow-icon {
  width: 0.75rem;
}

.arrow-icon {
  width: 1rem;
  height: auto;
}

.message-card {
  position: relative;
  padding: 0.75rem;
  padding-bottom: 3.5rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 16px 0px #0000000a;
  min-height: 6rem;
}

.message-card__textarea {
  width: 100%;
  height: 37px;
  min-height: 37px;
  max-height: 80px;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  padding-right: 7rem;
  resize: none;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.5;
  color: #00000054;
  box-sizing: border-box;
  outline: none;
}

.message-card__textarea::placeholder {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.5;
  color: #00000054;
}

.chat-thread {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-right: 0.25rem;
}

.chat-bubble {
  padding: 0.75rem 1rem;
  border-radius: 29px;
  background: #ffffff;
  max-width: 100%;
  word-break: break-word;
  white-space: pre-wrap;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.5;
  color: #00000054;
}

.chat-bubble--bot {
  align-self: flex-start;
}

.chat-bubble--user {
  align-self: flex-end;
}

.api-bubble {
  background: #ffffff;
  border-radius: 25px;
  padding: 2rem;
  max-width: 489px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.api-bubble__text {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 1;
  color: #363636;
}

.api-bubble__highlight {
  font-weight: 700;
}

.api-bubble__email {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 34px;
  line-height: 1;
  color: #d81b5d;
}

.message-card__send-btn {
  position: absolute;
  right: 0.75rem;
  bottom: 0.75rem;
  min-width: 85px;
  min-height: 34px;
  border-radius: 82px;
  padding: 13px 32px;
  background: #d81b5d;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0;
  border: none;
  cursor: pointer;
}

.top-menu-arrow.rotate-180 img {
  transform: rotate(180deg);
}

.contact-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 102px;
  border: none;
  background: #ffffff;
  box-shadow: 0px 4px 16px 0px #0000000a;
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #626873;
}

.contact-input__inner {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #626873;
}

.cta-btn {
  width: 262px;
  height: 57px;
  border-radius: 82px;
  padding: 13px 32px;
  background: #d81b5d;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0;
  border: none;
  cursor: pointer;
}

.about-us__card {
  width: 354px;
  max-width: 100%;
  height: 224px;
  border-radius: 24px;
  background: #101010;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem 1.5rem;
  text-align: left;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  letter-spacing: 0;
}

.about-us__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.about-us__card-title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 1;
  letter-spacing: 0;
}

.about-us__card-arrow {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.about-us__card-value {
  width: 100%;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 67px;
  line-height: 1;
  letter-spacing: 0;
}

.gallery {
  width: 100%;
}

.gallery__grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.gallery__mobile {
  width: 100%;
  position: relative;
}

.gallery__img {
  width: 100%;
  height: 270px;
  object-fit: cover;
  display: block;
}

.gallery__img--mobile {
  width: 100%;
}

.gallery__dots {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(4px);
}

.gallery__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0;
}

.gallery__dot--active {
  background: #d81b5d;
}

.contact {
  width: 100%;
  padding: 4rem 1rem;
  text-align: center;
  color: #363636;
}

.contact__title {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 48px;
  line-height: 1.1;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #363636;
  margin: 0;
}

.contact__desc {
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  color: #363636;
}

.contact__methods {
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.contact__card {
  flex: 1 1 180px;
  max-width: 240px;
  padding: 1.5rem 1.25rem;
  border-radius: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: #363636;
}

.contact__icon {
  height: 41px;
  width: auto;
  margin-bottom: 1.5rem;
}

.contact__card-btn {
  width: 172px;
  height: 41px;
  border-radius: 32px;
  background: #d81b5d;
  color: #ffffff;
  border: none;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1;
  letter-spacing: 0;
  text-transform: uppercase;
  cursor: pointer;
}

.contact__card-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
}

.contact__card-text-primary {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: 0;
  color: #000000;
}

.contact__card-text-secondary {
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: 0;
  color: #000000;
}

.contact__card-text-secondary {
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  font-size: 18px;
  line-height: 1.4;
  letter-spacing: 0;
  color: #000000;
}

@media (max-width: 1023px) {
  .gallery__grid {
    display: none;
  }

  .gallery__mobile {
    display: block;
  }

  .contact__methods {
    flex-direction: column;
  }

  .contact__card {
    max-width: 100%;
  }
}

@media (min-width: 1024px) {
  .gallery__mobile {
    display: none;
  }
}

.about-us__cta {
  border-radius: 32px;
}

.secondary-menu {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f1f2f6;
  border-radius: 36px;
  box-shadow: 0px 4px 16px 0px #0000000a;
  justify-content: center;
}

@media (max-width: 1023px) {
  .secondary-menu {
    display: none !important;
  }
}

.secondary-menu__item {
  flex: 1 1 auto;
  padding: 0.75rem 1.5rem;
  border-radius: 36px;
  border: none;
  background: transparent;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #000000;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}

.secondary-menu__item--active {
  background: #ffffff;
  border-radius: 66px;
  box-shadow: 0px 4px 16px 0px #0000000a;
}

.secondary-menu__mobile-btn {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 36px;
  background: #f1f2f6;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  color: #000000;
}
</style>

<route lang="yaml">
meta:
  layout: tw
</route>
